/**
 * 计划 / 预算 / 提醒枚举 —— F / G / H 三域共用的取值
 *
 * 依据：规格 §18（周期）· G 域 PRD `AC-G1.1`（预算周期）· H 域 PRD §H1 / §5.3.1（提醒）· 架构 §4.2
 */

// ─────────────────────── 周期规则种类 5 种 ───────────────────────

/**
 * 周期规则种类（`schedules.kind`）—— 5 种（规格 §18 · `AC-P1.11`）。
 *
 * 🔴 **P0 启用范围**（`DR-010`）：`Recurring` / `Amortization` / `Loan` **进 P0**；
 * `Installment`（信用卡分期）**延 P1** —— ✅ 枚举值**先定义**（延后不返工），
 * 但 **P0 代码不得产生该值**（`DR-026` D2：P0 不出现「分期确认」）。
 *
 * ## 🆕 `Template`（批 `132` · `DR-074`）—— **周期记账模板**
 *
 * 语义 = **到期直接记一笔「已经发生」的收支**（`settled`、计入收支、不需要销账），
 * 账户是"钱真的进出的那个账户"（储蓄卡 / 信用卡 / 电子钱包 / 社保公积金 / 投资账户），
 * 方向由金额符号表达（负 = `in`）。它承载的是 v1 迁来的那 6 条「每月固定扣款 / 固定入账」。
 *
 * ## 🔴 账户 : 规则 = 1:1 —— **只约束「账户从属型」三种**（`DR-074` `D2`）
 *
 * 1 个待缴账户 ↔ 1 条 `Recurring`；1 个待摊账户 ↔ 1 条 `Amortization`；
 * 1 个 `loan` 账户 ↔ 1 条 `Loan`；**重复绑定被拒**；`prepaid` 品类**无周期规则**。
 * ⇒ `Template` **是 1:N**（一张信用卡会同时扣保险、会员费、话费）——
 * 唯一性由 `uq_schedules_account` 的**部分索引**（`WHERE kind <> 'template'`）表达，
 * 判定收在 `nucleus/schedule.ts` 的 `bindsAccountOneToOne()` 一处。
 */
export const ScheduleKind = {
  Recurring: 'recurring',
  Installment: 'installment',
  Loan: 'loan',
  Amortization: 'amortization',
  /** 🆕 周期记账模板：到期直接记一笔已发生的收支（1:N · 挂任意实账户） */
  Template: 'template',
} as const;
export type ScheduleKind = (typeof ScheduleKind)[keyof typeof ScheduleKind];

// ─────────────────────── 周期频率 5 种 ───────────────────────

/**
 * 周期频率（`schedules` 的触发频率）—— 5 种（规格 §18）。
 *
 * 🔴 **月末为硬规则**：`Monthly` / `Quarterly` / `Yearly` 遇当月无该日时
 * **取该月最后一天**（clamp）—— v1 此处有缺陷（1/31 溢出到 3/3），v2 必须修正。
 */
export const ScheduleFrequency = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  Yearly: 'yearly',
} as const;
export type ScheduleFrequency = (typeof ScheduleFrequency)[keyof typeof ScheduleFrequency];

// ─────────────────────── 预算周期 4 种 ───────────────────────

/**
 * 预算周期（`budgets.period`）—— 4 种（G 域 PRD `AC-G1.1`：「枚举与规格一致」）。
 *
 * 🔴 唯一约束 `(ledgerId, categoryId, period)`（架构 §4.2，修 v1 无约束的缺陷）。
 * ⚠️ 注意与 `ScheduleFrequency` 的差别：**`Event` 只存在于预算**（事件型预算）。
 */
export const BudgetPeriod = {
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  Yearly: 'yearly',
  Event: 'event',
} as const;
export type BudgetPeriod = (typeof BudgetPeriod)[keyof typeof BudgetPeriod];

// ─────────────────────── 预算档位 3 档 ───────────────────────

/**
 * 预算档位（`budget_status_snapshot.level`）—— 3 档（架构 §4.2 / §5.2 `evaluateBudgetStatus()`）。
 *
 * 🔴 **G 算 H 拉**（不发事件）：G 域算档落快照，H 域读取比对 —— 提醒中心**不得自判档**。
 */
export const BudgetLevel = {
  Safe: 'safe',
  Warn: 'warn',
  Over: 'over',
} as const;
export type BudgetLevel = (typeof BudgetLevel)[keyof typeof BudgetLevel];

// ─────────────────────── 提醒类型 5 类 ───────────────────────

/**
 * 提醒类型（`reminder_instances.type`）—— 5 类（H 域 PRD §H1）。
 *
 * ⚠️ 语义权威 = H 域 PRD 的中文五类（**还款日 / 账单日 / 预算黄红 / 目标进度 / 记账习惯**）；
 * **英文标识由 R0 定名**（H 域 PRD 未给出标识符）—— 若 H 域后续给出不同标识，
 * **以 H 域为准并改此处**（本文件不是语义权威，只是**唯一载体**）。
 */
export const ReminderType = {
  /** 还款日（信用卡 / 贷款） */
  Repayment: 'repayment',
  /** 账单日 */
  Billing: 'billing',
  /** 预算黄红（档位变化） */
  Budget: 'budget',
  /** 目标进度 */
  Goal: 'goal',
  /** 记账习惯 */
  Habit: 'habit',
} as const;
export type ReminderType = (typeof ReminderType)[keyof typeof ReminderType];

// ─────────────────────── 提醒档位 3 档 ───────────────────────

/**
 * 提醒总开关档位（`user_preferences` 的提醒偏好组 / `reminder_prefs.level`）—— 3 档
 * （H 域 PRD §5.3.1：**仅关键 / 常规 / 全部**）。
 *
 * 🔴 **默认 = `Normal`**（架构 §4.2：不做「默认全开」再让用户一条条关）。
 * 🔴 **偏好存储与 UI 归 N 域**（`user_preferences` 表）；**H 域只读消费、禁自建第二份偏好表**。
 */
export const ReminderLevel = {
  /** 仅关键 */
  KeyOnly: 'key_only',
  /** 常规（默认） */
  Normal: 'normal',
  /** 全部 */
  All: 'all',
} as const;
export type ReminderLevel = (typeof ReminderLevel)[keyof typeof ReminderLevel];

// ─────────────────────── 提醒用户状态 4 态 ───────────────────────

/**
 * 提醒**用户状态**（`reminder_instances` 的 `readAt`/`doneAt`/`dismissedAt` 三列**派生**）—— 4 态。
 *
 * 依据：`DR-038`（提醒实例状态载体扩展）· H 域 PRD **§5.1.4**（三档分组）· **`AC-H1.10`** ·
 * **`AC-H3.7`**（关闭类型后「历史未读保留」）。
 *
 * 🔴 **不落库** —— 唯一实现 = 共享层 `nucleus/reminder.ts` 的 `reminderStateOf()`
 * （**顺序判定**：dismissed > done > read > unread）。三事件可叠加（读了之后才做完），
 * 存单枚举列会丢信息（`DR-038` §3）。
 */
export const ReminderState = {
  /** 未读 */
  Unread: 'unread',
  /** 已读（看过但未处理） */
  Read: 'read',
  /** 已完成（事情做完了） */
  Done: 'done',
  /** 已忽略（「不用提醒我」）—— ⚠️ P1 预留，本批（`56`）只读不写 */
  Dismissed: 'dismissed',
} as const;
export type ReminderState = (typeof ReminderState)[keyof typeof ReminderState];

// ─────────────────────── 提醒三档分组 3 组 ───────────────────────

/**
 * 提醒**展示分组**（原型 `09-提醒中心_原型.html`「需要你处理 / 知道就好 / 更早」）—— 3 组。
 *
 * 依据：H 域 PRD **§5.1.4**（对齐原型 · **不按时间倒序堆砌**）· **`AC-H1.10`**。
 *
 * 🔴 **不落库** —— 唯一实现 = `nucleus/reminder.ts` 的 `reminderGroupOf()`
 * （由 `type` + **状态**派生 ⇒ 加分组列 = 双实现，铁律 24）。
 */
export const ReminderGroup = {
  /** 需要你处理（有明确可执行动作且有时效） */
  Action: 'action',
  /** 知道就好（无需动作，或节奏正常） */
  Info: 'info',
  /** 更早（已读 / 已完成的历史） */
  Earlier: 'earlier',
} as const;
export type ReminderGroup = (typeof ReminderGroup)[keyof typeof ReminderGroup];

/**
 * 提醒**变体**（`windowKey` 的语义前缀）—— 🔴 用于**档位细分**与**动作派生**，**不落库**。
 *
 * 五类提醒里 `budget` / `habit` 各自有子形态（预算黄/红 · 周检视/查漏/每日记账），
 * 单靠 `ReminderType` 无法判「只推关键 vs 常规 vs 全部」（H 域 PRD §5.3.1）。
 */
export const ReminderVariant = {
  /** 还款日：提前量（`d-5`） */
  DueSoon: 'due_soon',
  /** 还款日：到期当天（`d-0`） */
  DueToday: 'due_today',
  /** 还款日：逾期（`od:<day>`） */
  Overdue: 'overdue',
  /** 信用卡账单：出账次日 */
  BillIssued: 'bill_issued',
  /** 预算：黄档 */
  BudgetWarn: 'warn',
  /** 预算：红档 */
  BudgetOver: 'over',
  /** 目标进度 */
  GoalProgress: 'goal',
  /** 记账习惯：周日检视 */
  WeeklyReview: 'weekly',
  /** 记账习惯：查漏补记 */
  CatchUp: 'catchup',
} as const;
export type ReminderVariant = (typeof ReminderVariant)[keyof typeof ReminderVariant];

// ─────────────────────── 储蓄目标的攒/取动作 2 种（I 域 · `DR-050`） ───────────────────────

/**
 * 攒钱动作（**不是交易类型** —— `AC-I2.10`）。
 *
 * 🔴 它只决定「两条腿谁 out、谁 in」，交易本身仍是最普通的 `transactions` +
 * 两条 `excludeFromStats = true` 的腿。**不得**据此新增交易类型枚举。
 */
export const GoalMove = {
  /** 攒一笔：来源账户 `out` · 目标宿主账户 `in` */
  Save: 'save',
  /** 取出：目标宿主账户 `out` · 去向账户 `in` */
  Withdraw: 'withdraw',
} as const;
export type GoalMove = (typeof GoalMove)[keyof typeof GoalMove];

/** 动作的中文名（**唯一文案源** —— 端侧与服务端都读它，禁止各写一份） */
export const GOAL_MOVE_LABEL: Readonly<Record<GoalMove, string>> = {
  [GoalMove.Save]: '攒一笔',
  [GoalMove.Withdraw]: '取出',
};

// ───────────────────── 目标自动攒的两种模式（I 域 × F3 · `DR-052` D4） ─────────────────────

/**
 * 目标自动攒模式。
 *
 * 🔴 **它不是交易类型**，只是"每期攒多少"的解释方式：`fixed` 看分、`percent` 看 E4。
 * 落账仍然只是 I 域的两条腿（`DR-050` D2）。
 */
export const AutoSaveMode = {
  /** 关闭（默认）—— 升级后既有目标**一律不自动攒** */
  Off: 'off',
  /** 每期固定额（`auto_save_value` = 分） */
  Fixed: 'fixed',
  /** 每期按本次净额的比例（`auto_save_value` = E4，10000 = 100%） */
  Percent: 'percent',
} as const;
export type AutoSaveMode = (typeof AutoSaveMode)[keyof typeof AutoSaveMode];

/** 模式的中文名（**唯一文案源**） */
export const AUTO_SAVE_MODE_LABEL: Readonly<Record<AutoSaveMode, string>> = {
  [AutoSaveMode.Off]: '不自动攒',
  [AutoSaveMode.Fixed]: '每期固定额',
  [AutoSaveMode.Percent]: '按比例',
};
